import React, { useState, useCallback, useEffect } from 'react';
import { FunnelStage, getFunnelStages } from '@/actions/data-fetch-funnel';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { toastMessage } from '@/utils/toast-message';
import { Filter } from 'lucide-react';
import FunnelStagesTable from './FunnelStagesTable';
import FunnelStageForm from './FunnelStageForm';

export const FunnelManagementClientWrapper: React.FC = () => {
  const [selectedStage, setSelectedStage] = useState<FunnelStage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState<FunnelStage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getFunnelStages();

      if (response.success && response.data) {
        // API returns data directly as array, not nested in data.data
        const stages = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setData(stages);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toastMessage(error.message, 'error');
      } else {
        toastMessage('An unknown error occurred', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (stage?: FunnelStage) => {
    setSelectedStage(stage || null);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedStage(null);
  };

  const handleSuccess = async (updatedStage: FunnelStage) => {
    // Update local state optimistically
    setData(prevData => {
      const index = prevData.findIndex(s => s.stage_id === updatedStage.stage_id);
      if (index !== -1) {
        // Update existing stage
        const newData = [...prevData];
        newData[index] = updatedStage;
        return newData;
      } else {
        // Add new stage
        return [...prevData, updatedStage];
      }
    });

    // Close modal
    setIsModalOpen(false);
    setSelectedStage(null);

    // Refresh data from server to ensure consistency
    await fetchData();

    toastMessage(
      selectedStage ? 'Funnel stage updated successfully' : 'Funnel stage created successfully',
      'success'
    );
  };

  const handleDelete = async () => {
    // Refresh data after deletion
    await fetchData();
  };

  return (
    <>
      <ModernSettingHeader
        title="Funnel Management"
        description="Configure and manage your sales funnel stages and status mappings"
        icon={<Filter className="h-6 w-6" />}
        buttonText="Add Stage"
        onClick={() => handleEdit()}
      />

      <div className="p-6">
        <FunnelStagesTable
          data={data}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={selectedStage ? 'Edit Funnel Stage' : 'Create Funnel Stage'}
        subtitle={selectedStage
          ? 'Update stage properties and visual settings'
          : 'Define a new stage in your sales funnel with custom properties'}
        icon={<Filter className="h-6 w-6" />}
      >
        <FunnelStageForm
          stage={selectedStage}
          onSuccess={handleSuccess}
          onCancel={handleClose}
        />
      </SettingsSlideModal>
    </>
  );
};
